/**
 * Prompt registry — catalogs all customizable prompts and resolves
 * user overrides vs defaults.
 *
 * Custom prompts use {{placeholder}} syntax for variable interpolation.
 */

import {
  validatePrompt,
  suggestPrompt,
  splitActivityPrompt,
  reflectPrompt,
  explainScorePrompt,
  goalCoveragePrompt,
  generateGapGoalsPrompt,
  activityCoveragePrompt,
  generateGapActivitiesPrompt,
} from './llm.prompts';

// ── Types ────────────────────────────────────────────────────────────────────

export type PromptKey =
  | 'validate_vision'
  | 'validate_goal'
  | 'validate_activity'
  | 'suggest_vision'
  | 'suggest_goal'
  | 'suggest_activity'
  | 'split_activity'
  | 'reflect'
  | 'explain_score'
  | 'goal_coverage'
  | 'generate_gap_goals'
  | 'activity_coverage'
  | 'generate_gap_activities';

export interface PromptDescriptor {
  key: PromptKey;
  label: string;
  category: 'Visions' | 'Goals' | 'Activities' | 'Reflections' | 'Analysis';
  description: string;
  placeholders: string[];
  placeholderDescriptions: Record<string, string>;
  getDefault: () => string;
}

// ── Catalog ──────────────────────────────────────────────────────────────────

export const PROMPT_CATALOG: PromptDescriptor[] = [
  // Visions
  {
    key: 'validate_vision',
    label: 'Validate Vision',
    category: 'Visions',
    description: 'Evaluates whether a vision statement is aspirational, clear, and inspiring.',
    placeholders: ['text'],
    placeholderDescriptions: { text: 'The vision statement text' },
    getDefault: () => validatePrompt('vision', '{{text}}'),
  },
  {
    key: 'suggest_vision',
    label: 'Suggest Vision Improvements',
    category: 'Visions',
    description: 'Generates 2-3 improved versions of a vision description.',
    placeholders: ['text'],
    placeholderDescriptions: { text: 'The current vision description' },
    getDefault: () => suggestPrompt('vision', '{{text}}'),
  },

  // Goals
  {
    key: 'validate_goal',
    label: 'Validate Goal',
    category: 'Goals',
    description: 'Evaluates a goal using SMART criteria (Specific, Measurable, Achievable, Relevant, Time-bound).',
    placeholders: ['text'],
    placeholderDescriptions: { text: 'The goal text' },
    getDefault: () => validatePrompt('goal', '{{text}}'),
  },
  {
    key: 'suggest_goal',
    label: 'Suggest Goal Improvements',
    category: 'Goals',
    description: 'Generates 2-3 improved SMART versions of a goal.',
    placeholders: ['text'],
    placeholderDescriptions: { text: 'The current goal text' },
    getDefault: () => suggestPrompt('goal', '{{text}}'),
  },

  // Activities
  {
    key: 'validate_activity',
    label: 'Validate Activity',
    category: 'Activities',
    description: 'Evaluates whether an activity is actionable, scoped, and completable.',
    placeholders: ['text'],
    placeholderDescriptions: { text: 'The activity text' },
    getDefault: () => validatePrompt('activity', '{{text}}'),
  },
  {
    key: 'suggest_activity',
    label: 'Suggest Activity Improvements',
    category: 'Activities',
    description: 'Generates 2-3 improved versions of an activity.',
    placeholders: ['text'],
    placeholderDescriptions: { text: 'The current activity text' },
    getDefault: () => suggestPrompt('activity', '{{text}}'),
  },
  {
    key: 'split_activity',
    label: 'Split Activity',
    category: 'Activities',
    description: 'Breaks an activity into 2-4 smaller sub-activities with time estimates.',
    placeholders: ['text', 'estimatedMinutes'],
    placeholderDescriptions: {
      text: 'The activity text',
      estimatedMinutes: 'Estimated duration in minutes',
    },
    getDefault: () => splitActivityPrompt('{{text}}', 60),
  },

  // Reflections
  {
    key: 'reflect',
    label: 'Daily Reflection',
    category: 'Reflections',
    description: 'Generates a supportive 2-3 sentence reflection and highlights from the daily score.',
    placeholders: ['scoreSummary'],
    placeholderDescriptions: { scoreSummary: 'Summary of daily productivity scores' },
    getDefault: () => reflectPrompt('{{scoreSummary}}'),
  },
  {
    key: 'explain_score',
    label: 'Explain Score',
    category: 'Reflections',
    description: 'Explains a daily productivity score in plain English with an actionable tip.',
    placeholders: ['scoreSummary'],
    placeholderDescriptions: { scoreSummary: 'Summary of daily productivity scores' },
    getDefault: () => explainScorePrompt('{{scoreSummary}}'),
  },

  // Analysis
  {
    key: 'goal_coverage',
    label: 'Goal Coverage Analysis',
    category: 'Analysis',
    description: 'Analyzes which dimensions of a vision are covered by goals and identifies gaps.',
    placeholders: ['visionText', 'goalList'],
    placeholderDescriptions: {
      visionText: 'The vision statement',
      goalList: 'Numbered list of goal titles',
    },
    getDefault: () => goalCoveragePrompt('{{visionText}}', ['{{goalList}}']),
  },
  {
    key: 'generate_gap_goals',
    label: 'Generate Gap Goals',
    category: 'Analysis',
    description: 'Creates SMART goals for uncovered dimensions of a vision.',
    placeholders: ['visionTitle', 'gapList'],
    placeholderDescriptions: {
      visionTitle: 'The vision title',
      gapList: 'Numbered list of gap dimensions',
    },
    getDefault: () => generateGapGoalsPrompt('{{visionTitle}}', ['{{gapList}}']),
  },
  {
    key: 'activity_coverage',
    label: 'Activity Coverage Analysis',
    category: 'Analysis',
    description: 'Analyzes which action areas of a goal are covered by activities and identifies gaps.',
    placeholders: ['goalTitle', 'goalDescription', 'activityList'],
    placeholderDescriptions: {
      goalTitle: 'The goal title',
      goalDescription: 'The goal description',
      activityList: 'Numbered list of activity titles',
    },
    getDefault: () => activityCoveragePrompt('{{goalTitle}}', '{{goalDescription}}', ['{{activityList}}']),
  },
  {
    key: 'generate_gap_activities',
    label: 'Generate Gap Activities',
    category: 'Analysis',
    description: 'Creates actionable activities for uncovered areas of a goal.',
    placeholders: ['goalTitle', 'goalDescription', 'gapList'],
    placeholderDescriptions: {
      goalTitle: 'The goal title',
      goalDescription: 'The goal description',
      gapList: 'Numbered list of gap areas',
    },
    getDefault: () => generateGapActivitiesPrompt('{{goalTitle}}', '{{goalDescription}}', ['{{gapList}}']),
  },
];

// ── Category grouping for the UI ─────────────────────────────────────────────

export interface PromptCategory {
  label: string;
  keys: PromptKey[];
}

export const PROMPT_CATEGORIES: PromptCategory[] = [
  { label: 'Vision Prompts', keys: ['validate_vision', 'suggest_vision'] },
  { label: 'Goal Prompts', keys: ['validate_goal', 'suggest_goal'] },
  { label: 'Activity Prompts', keys: ['validate_activity', 'suggest_activity', 'split_activity'] },
  { label: 'Reflections', keys: ['reflect', 'explain_score'] },
  { label: 'Coverage Analysis', keys: ['goal_coverage', 'generate_gap_goals', 'activity_coverage', 'generate_gap_activities'] },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

export function getDescriptor(key: PromptKey): PromptDescriptor | undefined {
  return PROMPT_CATALOG.find((d) => d.key === key);
}

/**
 * Interpolate {{placeholder}} tokens in a template string.
 */
function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) => vars[name] ?? `{{${name}}}`);
}

/**
 * Resolve a prompt — returns the custom override (interpolated) if one exists,
 * otherwise returns null so the caller can fall back to the original function.
 */
export function resolvePrompt(
  key: PromptKey,
  customPrompts: Record<string, string> | null | undefined,
  vars: Record<string, string>,
): string | null {
  const custom = customPrompts?.[key];
  if (!custom) return null;
  return interpolate(custom, vars);
}
